'use client'

import Link from 'next/link'
import { useId, useState } from 'react'
import { getAcquisition, track } from '@/lib/analytics'

// The extension only runs in Chrome on a computer, so on a phone the install
// button would lead nowhere. Instead the visitor can email themselves the
// link, and tick a box for one reminder three days later. Shown below the md
// breakpoint (pure CSS, so the static page needs no device check while it
// hydrates).

const apiBase = () => (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/+$/, '')
const UTM_KEYS = ['source', 'medium', 'campaign', 'term', 'content'] as const

// The tags of the visit that brought them, so the install on the computer is
// credited to it: this page's address first, else the latest stored visit.
function currentUtm(): Record<string, string> {
  const utm: Record<string, string> = {}
  const params = new URLSearchParams(window.location.search)
  for (const key of UTM_KEYS) {
    const value = params.get(`utm_${key}`)
    if (value) utm[key] = value
  }
  if (utm.source) return utm
  const last = getAcquisition()?.last
  if (!last?.source) return {}
  for (const key of UTM_KEYS) {
    if (last[key]) utm[key] = last[key] as string
  }
  return utm
}

export function DesktopLinkForm({ placement, light = false, className = '' }: { placement: string; light?: boolean; className?: string }) {
  const id = useId()
  const [email, setEmail] = useState('')
  const [remind, setRemind] = useState(false)
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle')
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('sending')
    setError('')
    try {
      const res = await fetch(`${apiBase()}/marketing/desktop-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), remind, landing: window.location.pathname, utm: currentUtm() }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data?.error || "We couldn't send the email. Try again in a moment.")
      track('desktop_link_requested', { placement, reminder: remind })
      setStatus('sent')
    } catch (err: any) {
      setError(err?.message || "We couldn't send the email. Try again in a moment.")
      setStatus('idle')
    }
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopied(true)
    } catch { }
  }

  const muted = light ? 'text-primary-100' : 'text-gray-500'

  return (
    <div className={`md:hidden max-w-md mx-auto rounded-xl border p-5 text-left ${light ? 'border-white/30 bg-white/10 text-white' : 'border-primary-200 bg-white text-gray-700'} ${className}`}>
      <p className="font-semibold">CVCircuit works in Chrome on a laptop or desktop.</p>

      {status === 'sent' ? (
        <div className="mt-2" role="status">
          <p className={`text-sm ${light ? 'text-white' : 'text-gray-700'}`}>
            Sent to <span className="font-semibold break-all">{email.trim()}</span>. Open it on your computer to install CVCircuit.
          </p>
          <p className={`mt-2 text-sm ${muted}`}>
            {remind ? "We'll send one reminder in 3 days. " : ''}Not there? Check your spam folder.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-1">
          <p className={`text-sm ${muted}`}>Send yourself the link and open it on your computer.</p>
          <label htmlFor={`${id}-email`} className="sr-only">Your email address</label>
          <input
            id={`${id}-email`}
            type="email"
            required
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email address"
            className="mt-4 w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-base text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
          <label htmlFor={`${id}-remind`} className="mt-3 flex items-start gap-3 text-sm cursor-pointer">
            <input
              id={`${id}-remind`}
              type="checkbox"
              checked={remind}
              onChange={(e) => setRemind(e.target.checked)}
              className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 accent-primary-600"
            />
            <span>Also send me one reminder in 3 days</span>
          </label>
          {error && <p className={`mt-3 text-sm font-medium ${light ? 'text-white' : 'text-red-600'}`} role="alert">{error}</p>}
          <button
            type="submit"
            disabled={status === 'sending'}
            className={`mt-4 w-full rounded-lg px-5 py-3 text-sm font-semibold transition-colors disabled:opacity-60 ${light ? 'bg-white text-primary-600' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
          >
            {status === 'sending' ? 'Sending…' : 'Email me the link'}
          </button>
          <p className={`mt-3 text-xs ${muted}`}>
            We only use your email to send this link, and the reminder if you tick the box, then delete it.{' '}
            <Link href="/privacy" className="underline">Privacy policy</Link>
          </p>
        </form>
      )}

      <div className="mt-4 flex flex-col gap-2">
        <button
          type="button"
          onClick={copy}
          className={`rounded-lg border px-5 py-2.5 text-sm font-semibold ${light ? 'border-white/40 text-white' : 'border-gray-300 text-gray-700'}`}
        >
          {copied ? 'Link copied' : 'Copy the link instead'}
        </button>
        <Link
          href="/cv/start"
          className={`rounded-lg border px-5 py-2.5 text-center text-sm font-semibold ${light ? 'border-white/40 text-white' : 'border-gray-300 text-gray-700'}`}
        >
          Start my CV on this phone
        </Link>
      </div>
    </div>
  )
}
